<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { FairHeader } from '@/ui/patterns/FairHeader'
import { FairAnchoredMenu } from '@/ui/patterns/FairAnchoredMenu'
import { FairLocaleMenu } from '@/ui/patterns/FairLocaleMenu'
import { FairAccountMenu } from '@/ui/patterns/FairAccountMenu'
import { FairNotificationsSheet } from '@/ui/patterns/FairNotificationsSheet'
import { FairAudienceSheet } from '@/ui/patterns/FairAudienceSheet'
import { useImageSearch } from '@/features/search/store'
import type { FairTabItem } from '@/ui/tokens/tabs.data'
import { useFairHomeHeaderActions } from './useFairHomeHeaderActions'
import { useFairHomeSearchNavigate } from './useFairHomeSearchNavigate'
import {
  FAIR_HOME_HERO_AUDIENCE_TAB_VALUES,
  getFairHomeHeroHeaderActions,
  getFairHomeHeroHeaderSize,
} from './fair-home-hero.data'
import {
  FAIR_HOME_STICKY_DISSOLVE_MS,
  FAIR_HOME_STICKY_HEADER_HEIGHT_PX,
  canFairHomeStickyChrome,
} from './fair-home-sticky.data'
import type {
  FairMegaMenuBrandFeatured,
  FairMegaMenuContent,
  FairMegaMenuLink,
} from '@/ui/tokens/fair-mega-menu.data'
import {
  getFairHomeMegaMenuPanels,
  getFairHomeMegaMenuTabs,
} from './fair-home-i18n'
import { navigateFairMegaMenuSelectionLink } from './navigateFairMegaMenuSelectionLink'
import {
  navigateFairMegaMenuBrandFeatured,
  navigateFairMegaMenuBrandLetter,
  navigateFairMegaMenuBrandLink,
  navigateFairMegaMenuBrandsViewAll,
} from '@/widgets/brands/fair'
import './fair-home-chrome.css'

const props = withDefaults(
  defineProps<{
    pinned?: boolean
    megaCollapsed?: boolean
  }>(),
  {
    pinned: false,
    megaCollapsed: false,
  },
)

const emit = defineEmits<{
  'mega-panel-open': []
  'mega-panel-close': []
}>()

const audience = defineModel<string>('audience', { default: 'women' })
const searchOpen = defineModel<boolean>('searchOpen', { default: false })

const { t } = useI18n()
const router = useRouter()

const searchQuery = ref('')
const audienceSheetVisible = ref(false)
const rootEl = ref<HTMLElement | null>(null)
const headerHeight = ref(FAIR_HOME_STICKY_HEADER_HEIGHT_PX)
const viewportWidth = ref(
  typeof window !== 'undefined' ? window.innerWidth : 1920,
)

const imageSearch = useImageSearch()
const { navigateToSearch } = useFairHomeSearchNavigate()

const headerSize = computed(() => getFairHomeHeroHeaderSize(viewportWidth.value))

const stickyEnabled = computed(() =>
  canFairHomeStickyChrome(viewportWidth.value),
)

const headerPinned = computed(
  () => stickyEnabled.value && props.pinned,
)

const headerTheme = computed(() =>
  headerPinned.value ? 'dark' : 'light',
)

const headerTabs = computed<FairTabItem[]>(() =>
  FAIR_HOME_HERO_AUDIENCE_TAB_VALUES.map((value) => ({
    value,
    label: t(`audience.${value}`),
  })),
)

const headerActions = computed(() => getFairHomeHeroHeaderActions(t))

const megaMenuTabs = computed(() => getFairHomeMegaMenuTabs(t))
const megaMenuPanels = computed(() => getFairHomeMegaMenuPanels(t))

const rootClass = computed(() => ({
  'fair-home-chrome-header': true,
  'fair-home-chrome-header--sticky': stickyEnabled.value,
  'fair-home-chrome-header--pinned': headerPinned.value,
  'fair-home-chrome-header--mega-collapsed':
    headerPinned.value && props.megaCollapsed,
}))

const {
  localeMenuRef,
  accountMenuRef,
  localeMenuVisible,
  accountMenuVisible,
  notificationsVisible,
  userName,
  avatarUrl,
  notificationItems,
  notificationsLoading,
  notificationsError,
  handleActionClick,
  handleLocaleLanguageSelected,
  handleAccountNavigate,
  handleAccountLogout,
  handleNotificationItemClick,
  handleNotificationsViewAll,
} = useFairHomeHeaderActions()

let settleMeasureTimer = 0

function publishChromeHeights(height: number) {
  headerHeight.value = height
  document.documentElement.style.setProperty(
    '--fair-home-chrome-header-height',
    `${height}px`,
  )
}

function measureHeaderHeight() {
  const el = rootEl.value
  if (!el) return
  publishChromeHeights(Math.max(el.getBoundingClientRect().height, 60))
}

/** One settle measure after dissolve — cancels prior timers (anti feedback loop). */
function scheduleSettleMeasure(delayMs = FAIR_HOME_STICKY_DISSOLVE_MS) {
  if (settleMeasureTimer) {
    window.clearTimeout(settleMeasureTimer)
    settleMeasureTimer = 0
  }
  settleMeasureTimer = window.setTimeout(() => {
    settleMeasureTimer = 0
    measureHeaderHeight()
  }, delayMs)
}

function updateViewportWidth() {
  viewportWidth.value = window.innerWidth
  void nextTick(measureHeaderHeight)
}

async function handleSearch(query: string) {
  searchQuery.value = query
  await navigateToSearch(query)
}

function handleAddPhoto() {
  if (viewportWidth.value < 1024) {
    imageSearch.fairMobileImageFlowEntry = 'camera'
    imageSearch.isFairMobileImageFlowOpen = true
    return
  }

  imageSearch.fairDesktopImageFlowEntry = 'upload'
  imageSearch.isFairDesktopImageFlowOpen = true
}

function handleImageFile(file: File) {
  imageSearch.pendingImageFile = file

  if (viewportWidth.value < 1024) {
    imageSearch.fairMobileImageFlowEntry = 'camera'
    imageSearch.isFairMobileImageFlowOpen = true
    return
  }

  imageSearch.fairDesktopImageFlowEntry = 'upload'
  imageSearch.isFairDesktopImageFlowOpen = true
}

function handleAudienceOpen() {
  audienceSheetVisible.value = true
}

function handleMegaLinkClick(link: FairMegaMenuLink) {
  if (navigateFairMegaMenuSelectionLink(router, link, audience.value)) {
    return
  }
  navigateFairMegaMenuBrandLink(router, link, audience.value)
}

function handleMegaViewAllClick(
  section: 'left' | 'right',
  type: FairMegaMenuContent['type'],
) {
  if (type !== 'brands' || section !== 'left') return
  navigateFairMegaMenuBrandsViewAll(router, audience.value)
}

function handleMegaLetterClick(letter: string) {
  navigateFairMegaMenuBrandLetter(router, letter, audience.value)
}

function handleMegaBrandFeaturedClick(item: FairMegaMenuBrandFeatured) {
  navigateFairMegaMenuBrandFeatured(router, item, audience.value)
}

watch(headerPinned, async () => {
  await nextTick()
  scheduleSettleMeasure()
})

watch(searchOpen, async () => {
  await nextTick()
  scheduleSettleMeasure()
})

watch(
  () => props.megaCollapsed,
  async () => {
    await nextTick()
    scheduleSettleMeasure()
  },
)

onMounted(() => {
  publishChromeHeights(
    stickyEnabled.value ? FAIR_HOME_STICKY_HEADER_HEIGHT_PX : 60,
  )
  updateViewportWidth()
  window.addEventListener('resize', updateViewportWidth, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewportWidth)
  if (settleMeasureTimer) {
    window.clearTimeout(settleMeasureTimer)
    settleMeasureTimer = 0
  }
})

defineExpose({ headerHeight, measureHeaderHeight })
</script>

<template>
  <div
    ref="rootEl"
    :class="rootClass"
  >
    <FairHeader
      v-model:audience="audience"
      v-model:search-open="searchOpen"
      v-model:search-query="searchQuery"
      :theme="headerTheme"
      :size="headerSize"
      :tabs="headerTabs"
      :actions="headerActions"
      :search-placeholder="t('fair_home.header.search_placeholder')"
      :add-photo-label="t('fair_home.header.add_photo')"
      :submit-label="t('fair_home.header.submit')"
      :mega-menu-tabs="megaMenuTabs"
      :mega-menu-panels="megaMenuPanels"
      :show-search-toggle="stickyEnabled"
      :show-horizontal-menu="stickyEnabled"
      :show-mega-menu="stickyEnabled"
      @action-click="handleActionClick"
      @search="handleSearch"
      @add-photo="handleAddPhoto"
      @image-file="handleImageFile"
      @audience-open="handleAudienceOpen"
      @mega-panel-open="emit('mega-panel-open')"
      @mega-panel-close="emit('mega-panel-close')"
      @mega-link-click="handleMegaLinkClick"
      @mega-view-all-click="handleMegaViewAllClick"
      @mega-letter-click="handleMegaLetterClick"
      @mega-brand-featured-click="handleMegaBrandFeaturedClick"
    />
  </div>

  <FairAudienceSheet
    v-model:visible="audienceSheetVisible"
    v-model="audience"
    :title="t('fair_home.audience_sheet.title')"
    :options="headerTabs"
  />

  <FairAnchoredMenu
    ref="localeMenuRef"
    v-model:visible="localeMenuVisible"
  >
    <FairLocaleMenu
      @select-language="handleLocaleLanguageSelected"
    />
  </FairAnchoredMenu>

  <FairAnchoredMenu
    ref="accountMenuRef"
    v-model:visible="accountMenuVisible"
  >
    <FairAccountMenu
      :user-name="userName"
      :avatar-url="avatarUrl"
      @navigate="handleAccountNavigate"
      @logout="handleAccountLogout"
    />
  </FairAnchoredMenu>

  <FairNotificationsSheet
    v-model:visible="notificationsVisible"
    :title="t('fair_home.notifications.title')"
    :empty-label="t('fair_home.notifications.empty')"
    :error-label="t('fair_home.notifications.error')"
    :loading-label="t('fair_home.notifications.loading')"
    :view-all-label="t('fair_home.notifications.view_all')"
    :items="notificationItems"
    :loading="notificationsLoading"
    :error="notificationsError"
    @item-click="handleNotificationItemClick"
    @view-all="handleNotificationsViewAll"
  />
</template>
